import React, { useEffect, useState, useRef } from "react";
import { Box, Divider, TableCell, Tooltip } from "@mui/material";
import CommonCalculator from "../../../builders/calculator/common";
import PdfGenerator from "./Pdf/OtherIncomePdf";
import {
  formatAmount,
  formatHistoryAmount,
  isAutoload
} from "../../../utils/hooks/dataHandler";
import ScheduleHeader from "../../App/ScheduleHeader";
import ScheduleNotes from "../ScheduleNotes";
import CommonTable, { ReadOnlyCell } from "../../utils/CommonTable";
import {
  TextFieldForName,
  TextFieldForViewOnly
} from "../../utils/TextField/TextField";
import { CalculatorTextField } from "../../utils/TextField/NumericTextField";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import IconButtonWithTooltip from "../../utils/IconButtonWithTooltip";
import SectionBox from "../utils/SectionBox";
import ScheduleContainer from "../utils/ScheduleContainer";
import ForeignIncomeForm from "../utils/ForeignIncomeForm";
import CustomDialog from "../../utils/dialog/customDialog";

const OtherIncomeSchedule = ({
  company,
  schedule,
  scheduleDetails,
  saveSchedule,
  deleteSchedule,
  canEditSchedule = true
}) => {
  const [schName, setSchName] = useState("Other Income");
  const [taxAdjustment, setTaxAdjustment] = useState("");
  const [notes, setNotes] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [unremittedSchNo, setUnremittedSchNo] = useState("");
  const [ftcSchNo, setFtcSchNo] = useState("");
  const [otherIncomeRows, setOtherIncomeRows] = useState([]);
  const [expensesRows, setExpensesRows] = useState([]);
  const [totalOtherIncome, setTotalOtherIncome] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [netOtherIncome, setNetOtherIncome] = useState(0);

  const [isTagToFTC, setIsTagToFTC] = useState(false);
  const [foreignTaxSuffered, setForeignTaxSuffered] = useState(0);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const [showFTCSaveDialog, setShowFTCSaveDialog] = useState(false);
  const [shareOfExpenses, setShareOfExpenses] = useState(null);
  const ftcFormRef = useRef(null);
  const handleTagToFTC = () => {
    setIsTagToFTC(true);
  };

  const handleUnTagToFTC = () => {
    setIsTagToFTC(false);
  };

  const scrollToFTCForm = () => {
    if (ftcFormRef.current) {
      ftcFormRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  };

  const isOtherIncome = (title) => title === "Other Income";
  const isForeignTradeBranch = () => schedule.type === "Foreign Trade / Branch";

  const getTableColumns = (handleChange, setRows, title) => [
    {
      key: "description",
      label: "Description",
      headerAlign: "left",
      render: (row, index) => (
        <>
          {!canEditSchedule ||
          (isOtherIncome(title) && isForeignTradeBranch()) ? (
            <ReadOnlyCell
              value={row.description}
              width='100%'
              textAlign='left'
            />
          ) : (
            <TextFieldForName
              value={row.description}
              onChange={handleChange}
              disabled={isAutoload(row.status)}
              reference={{
                index: index,
                field: "description"
              }}
            />
          )}
        </>
      )
    },
    {
      key: "amount",
      label: "Amount",
      headerAlign: "right",
      colAlign: "right",
      render: (row, index) => (
        <>
          {!canEditSchedule ||
          (isOtherIncome(title) && isForeignTradeBranch()) ? (
            <ReadOnlyCell
              value={formatAmount(row.amount)}
              width='100%'
              textAlign='right'
            />
          ) : (
            <Tooltip
              title={
                row?.historyAmount &&
                row.historyAmount.length > 0 &&
                formatHistoryAmount(row.historyAmount)
              }
              placement='top'
            >
              <Box display='inline-block' sx={{ width: "100%" }}>
                <CalculatorTextField
                  value={row?.historyAmount || []}
                  onChange={handleChange}
                  reference={{
                    index: index,
                    field: "amount"
                  }}
                  fullWidth={true}
                  disabled={isAutoload(row.status)}
                />
              </Box>
            </Tooltip>
          )}
        </>
      )
    },
    {
      key: "reference",
      label: "Reference",
      headerAlign: "center",
      render: (row, index) => (
        <TextFieldForName
          value={row?.reference}
          onChange={handleChange}
          reference={{
            index: index,
            field: "reference"
          }}
          canEdit={canEditSchedule}
          viewOnlyAlign='center'
        />
      )
    },
    {
      key: "source",
      label: "Sch",
      headerAlign: "center",
      render: (row) => <TextFieldForViewOnly value={row.source?.sch_no || ""} />
    },
    canEditSchedule
      ? {
          key: "action",
          label: "Action",
          headerAlign: "center",
          render: (row, index) => (
            <IconButtonWithTooltip
              icon={DeleteOutlineIcon}
              tooltipTitle='Delete Row'
              onClick={() => removeRow(setRows, index)}
              color='error'
              size='small'
              disabled={
                isAutoload(row.status) ||
                (isOtherIncome(title) && isForeignTradeBranch())
              }
            />
          )
        }
      : []
  ];

  const calculateTotals = (expensesData, incomeData) => {
    const c_totalExpenses = CommonCalculator.arraySum(expensesData);
    const c_totalOtherIncome = CommonCalculator.arraySum(incomeData);
    const netIncome = c_totalOtherIncome - c_totalExpenses;

    setTotalOtherIncome(c_totalOtherIncome);
    setTotalExpenses(c_totalExpenses);
    setNetOtherIncome(netIncome);
  };

  const handleRowChange = (value, { index, field, rowType }) => {
    const isExpense = rowType === "expense";
    const rows = isExpense ? expensesRows : otherIncomeRows;
    const setRows = isExpense ? setExpensesRows : setOtherIncomeRows;
    const otherRows = isExpense ? otherIncomeRows : expensesRows;

    const updatedRows = [...rows];
    if (field === "amount") {
      updatedRows[index]["historyAmount"] = value;
      updatedRows[index][field] = value.reduce((sum, num) => sum + num, 0);
      calculateTotals(
        isExpense ? updatedRows : otherRows,
        isExpense ? otherRows : updatedRows
      );
    } else {
      updatedRows[index][field] = value;
    }
    setRows(updatedRows);
  };

  const handleOtherIncomeChange = (value, params) => {
    handleRowChange(value, { ...params, rowType: "income" });
  };

  const handleExpenseChange = (value, params) => {
    handleRowChange(value, { ...params, rowType: "expense" });
  };

  const proceedSave = (resetFTC = false) => {
    const effectiveForeignTaxSuffered = resetFTC ? 0 : foreignTaxSuffered;
    const effectiveIsTagToFTC = resetFTC ? false : isTagToFTC;

    if (resetFTC) {
      setForeignTaxSuffered(0);
      setIsTagToFTC(false);
    }

    schedule.name = schName;
    schedule.taxAdjustment = taxAdjustment;
    schedule.notes = notes;
    schedule.reviewNotes = reviewNotes;

    saveSchedule(schedule, {
      otherIncomeRows: otherIncomeRows,
      expensesRows: expensesRows,
      totalAdjustment: netOtherIncome,
      totalRevenue: totalOtherIncome,
      totalExpenses: totalExpenses,
      foreignTaxSuffered: effectiveForeignTaxSuffered,
      isTagToFTC: effectiveIsTagToFTC,
      doc: JSON.stringify({
        taxAdjustment,
        otherIncomeRows,
        expensesRows,
        totalOtherIncome,
        totalExpenses,
        netOtherIncome,
        foreignTaxSettings: {
          foreignTaxSuffered: effectiveForeignTaxSuffered,
          isTagToFTC: effectiveIsTagToFTC,
          unremittedSchNo: unremittedSchNo,
          ftcSchNo: ftcSchNo
        },
        notes,
        reviewNotes
      })
    });
  };

  const handleSave = () => {
    const isUnremittedForeignIncome =
      taxAdjustment === "Unremitted Foreign Income";
    const shouldResetFTC = isUnremittedForeignIncome && netOtherIncome <= 0;
    const shouldShowFTCHint =
      isUnremittedForeignIncome &&
      netOtherIncome > 0 &&
      foreignTaxSuffered === 0;

    if (shouldResetFTC) {
      setConfirmResetOpen(true);
      return;
    }

    if (shouldShowFTCHint) {
      setShowFTCSaveDialog(true);
      return;
    }

    proceedSave(false);
  };

  useEffect(() => {
    //init
    if (schedule) {
      setSchName(schedule.name);
      setTaxAdjustment(schedule.taxAdjustment);
      setNotes(schedule.notes);
      setReviewNotes(schedule.reviewNotes);
    }

    if (scheduleDetails) {
      setExpensesRows(
        scheduleDetails.expensesRows.map((row) => {
          if (row.amount < 0) {
            row.amount = -row.amount;
          }

          if (!row.historyAmount || row.historyAmount.length <= 0) {
            row.historyAmount = [row.amount];
          }
          return row;
        })
      );
      setOtherIncomeRows(
        scheduleDetails.otherIncomeRows.map((row) => {
          if (!row.historyAmount || row.historyAmount.length <= 0) {
            row.historyAmount = [row.amount];
          }
          return row;
        })
      );

      setForeignTaxSuffered(scheduleDetails.foreignTaxSuffered || 0);
      setIsTagToFTC(scheduleDetails.isTagToFTC || false);
      setUnremittedSchNo(scheduleDetails.schRef || "");
      setFtcSchNo(scheduleDetails.foreignTaxCreditScheduleRef || "");
      setShareOfExpenses(scheduleDetails.shareOfExpenses || null);

      //calculation
      calculateTotals(
        scheduleDetails.expensesRows,
        scheduleDetails.otherIncomeRows
      );
    }
  }, []);

  const addRow = (setRows) => {
    setRows((prevRows) => [
      ...prevRows,
      {
        description: "",
        amount: "",
        reference: ""
      }
    ]);
  };

  const removeRow = (setRows, index) => {
    setRows((prevRows) => prevRows.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ padding: 4 }}>
      <ScheduleHeader
        {...{
          title: schedule.type,
          companyName: company.name,
          companyType: company.companyType,
          uen: company.uen,
          yearOfAssessment: company.yearOfAssessment,
          financialYearStart: company.financialYearStart,
          financialYearEnd: company.financialYearEnd,
          currency: company.currency,
          handleSave,
          deleteSchedule,
          canEditSchedule
        }}
      >
        <PdfGenerator
          schedule={schedule}
          company={company}
          otherIncomeRows={otherIncomeRows}
          expensesRows={expensesRows}
          totalOtherIncome={totalOtherIncome}
          totalExpenses={totalExpenses}
          netOtherIncome={netOtherIncome}
          foreignTaxSettings={{
            foreignTaxSuffered,
            isTagToFTC,
            unremittedSchNo,
            ftcSchNo
          }}
          notes={notes}
          reviewNotes={reviewNotes}
          shareOfExpenses={shareOfExpenses}
        />
      </ScheduleHeader>
      <br />

      <ScheduleContainer
        key={schedule.id}
        id={schedule.id}
        schedule={schedule}
        schName={schName}
        taxAdjustment={taxAdjustment}
        allowedToEditSchName={canEditSchedule}
        allowedToAdjustTax={canEditSchedule}
        taxAdjustOptions={["", "Added Back", "Unremitted Foreign Income"]}
        updateHeading={(newTitle) => {
          schedule.name = newTitle;
          setSchName(newTitle);
        }}
        updateAdjustment={(newAdjustment) => {
          schedule.taxAdjustment = newAdjustment;
          if (newAdjustment !== "Unremitted Foreign Income") {
            setIsTagToFTC(false);
            setForeignTaxSuffered(0);
          }
          setTaxAdjustment(newAdjustment);
        }}
        canEditSchedule={canEditSchedule}
      >
        {(fullScreenMode) => (
          <>
            {/* Other Rental Income */}
            <SectionBox
              title={isForeignTradeBranch() ? "Revenue" : "Other Income"}
              onAdd={
                isForeignTradeBranch()
                  ? undefined
                  : () => addRow(setOtherIncomeRows)
              }
              canEdit={canEditSchedule}
            >
              <CommonTable
                rows={otherIncomeRows}
                columns={getTableColumns(
                  handleOtherIncomeChange,
                  setOtherIncomeRows,
                  "Other Income"
                )}
              >
                <TableCell
                  align='right'
                  sx={{
                    fontWeight: "bold"
                  }}
                >
                  Total Other Income
                </TableCell>
                <TableCell
                  align='right'
                  sx={{
                    fontWeight: "bold"
                  }}
                >
                  {formatAmount(totalOtherIncome)}
                </TableCell>
                <TableCell colSpan={3} />
              </CommonTable>
            </SectionBox>

            <Divider sx={{ marginY: 4 }} />
            {/* Rental Expenses */}
            <SectionBox
              title='Direct Expenses'
              onAdd={() => addRow(setExpensesRows)}
              canEdit={canEditSchedule}
            >
              <CommonTable
                rows={expensesRows}
                columns={getTableColumns(
                  handleExpenseChange,
                  setExpensesRows,
                  "Direct Expenses"
                )}
              >
                <TableCell
                  align='right'
                  sx={{
                    fontWeight: "bold"
                  }}
                >
                  Total Expenses
                </TableCell>
                <TableCell
                  align='right'
                  sx={{
                    fontWeight: "bold"
                  }}
                >
                  {formatAmount(totalExpenses)}
                </TableCell>
                <TableCell colSpan={3} />
              </CommonTable>
            </SectionBox>
          </>
        )}
      </ScheduleContainer>

      <Divider sx={{ marginY: 4 }} />

      <Box ref={ftcFormRef}>
        <ForeignIncomeForm
          canEdit={canEditSchedule}
          steps={[
            {
              section: "Other Income",
              amount: formatAmount(totalOtherIncome),
              tooltip:
                "Total income from other sources. This includes all non-primary business income."
            },
            {
              section: "Direct Expenses",
              amount: `(${formatAmount(totalExpenses)})`,
              tooltip:
                "Total expenses directly related to generating the other income. These expenses are deducted from the other income."
            }
          ]}
          total={{
            title: "Net Other Income",
            amount: formatAmount(netOtherIncome),
            tooltip:
              "Formula: Other Income - Direct Expenses. This represents the net amount of other income after deducting related expenses."
          }}
          isTagToFTC={isTagToFTC}
          taxAdjustment={taxAdjustment}
          grossIncome={totalOtherIncome}
          allowableExpenses={totalExpenses}
          netOtherIncome={netOtherIncome}
          foreignTaxSuffered={foreignTaxSuffered}
          onForeignTaxSufferedChange={(taxAmount) => {
            setForeignTaxSuffered(taxAmount);
          }}
          onTagToFTC={handleTagToFTC}
          onUnTagToFTC={handleUnTagToFTC}
        />
      </Box>

      <ScheduleNotes
        notes={notes}
        setNotes={setNotes}
        reviewNotes={reviewNotes}
        setReviewNotes={setReviewNotes}
        enableEdit={canEditSchedule}
      />

      <CustomDialog
        open={confirmResetOpen}
        onClose={() => setConfirmResetOpen(false)}
        onConfirm={() => {
          setConfirmResetOpen(false);
          proceedSave(true);
        }}
        title={"Reset Foreign Tax Credit?"}
        description={
          "Net income is zero or negative. Saving will reset Foreign Tax Suffered and remove FTC tagging for this schedule. Do you want to continue?"
        }
        type='warning'
      />

      {/* FTC Save Dialog */}
      <CustomDialog
        open={showFTCSaveDialog}
        onClose={() => {
          setShowFTCSaveDialog(false);
          // Scroll after dialog is closed to prevent interference
          setTimeout(() => {
            scrollToFTCForm();
          }, 100);
        }}
        onConfirm={() => {
          setShowFTCSaveDialog(false);
          proceedSave(false);
        }}
        title={"Has the foreign income suffered foreign tax?"}
        description={
          "Please indicate the amount of foreign tax suffered (if any)​ in the 'FTC Processing' section before saving. If no foreign tax was suffered, click 'Confirm' to save. Otherwise, click 'Cancel' to input the amount of foreign tax suffered."
        }
        type='info'
      />
    </Box>
  );
};

export default OtherIncomeSchedule;
